import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveSeekerIdNum } from '../../lib/adminViewAs';
import { getProjectsForSeeker, getProjectDisplayName, type Project } from '@data/projects';
import { getOffersForSeeker, ensureProjectForOffer } from '@services/offers';
import { getPlans, getPlanById } from '@services/plans';
import { getPublicPlans } from '@data/publicPlans';
import {
	readPlanThumbSession,
	readSeekerPlansSession,
	readSeekerProjectsSession,
	writePlanThumbSession,
	writeSeekerPlansSession,
	writeSeekerProjectsSession
} from '@services/projectsSessionCache';
import { getImageUrl } from '@utils/imageUrl';
import { useI18n } from '../../i18n';

type ListScope = 'active' | 'archive' | 'all';

function projectMatchesListScope(project: Project, scope: ListScope): boolean {
	if (scope === 'all') return true;
	if (scope === 'active') {
		return project.status !== 'completed' && project.status !== 'cancelled';
	}
	return project.status === 'completed' || project.status === 'cancelled';
}

export default function SeekerProjects() {
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const [projects, setProjects] = useState<Project[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	// Plans for image lookup (merged DB + local)
	const [plansForImages, setPlansForImages] = useState<Array<{ id: number; after_image_url?: string; before_image_url?: string }>>([]);
	// Fallback: image URL per project when plan not in plansForImages (e.g. closed plans)
	const [projectImageUrls, setProjectImageUrls] = useState<Record<string, string>>({});
	const [listScope, setListScope] = useState<ListScope>('active');

	const seekerUserIdNum = useMemo(() => {
		const n = getAdminEffectiveSeekerIdNum(user, viewAsTarget);
		return n == null ? NaN : n;
	}, [user, viewAsTarget]);

	useLayoutEffect(() => {
		if (!user?.id || Number.isNaN(seekerUserIdNum)) {
			setProjects([]);
			setPlansForImages([]);
			setIsLoading(false);
			return;
		}
		const pCached = readSeekerProjectsSession(seekerUserIdNum);
		const plansCached = readSeekerPlansSession(seekerUserIdNum);
		if (plansCached) setPlansForImages(plansCached);
		if (pCached !== undefined) {
			setProjects(pCached);
			setIsLoading(false);
		} else {
			setIsLoading(true);
		}
	}, [user?.id, seekerUserIdNum, viewAsTarget]);

	useEffect(() => {
		if (!user?.id || Number.isNaN(seekerUserIdNum)) {
			setIsLoading(false);
			return;
		}

		let cancelled = false;
		const userIdNum = seekerUserIdNum;

		const loadPlansForImages = async () => {
			const dbPlans = await getPlans(userIdNum).catch(() => []);
			const localPlans = getPublicPlans().filter((p) => p.seekerUserId === String(userIdNum));
			const merged = [
				...dbPlans.map((p) => ({ id: p.id, after_image_url: p.after_image_url, before_image_url: p.before_image_url })),
				...localPlans
					.filter((p) => p.databaseId)
					.map((p) => ({
						id: p.databaseId!,
						after_image_url: p.aiGenerated?.photoAfterUrl,
						before_image_url: p.photoBeforeUrl
					}))
			];
			if (!cancelled) {
				setPlansForImages(merged);
				writeSeekerPlansSession(userIdNum, merged);
			}
		};
		void loadPlansForImages();

		const loadAndRepair = async () => {
			const seekerOffers = await getOffersForSeeker(userIdNum).catch(() => []);
			if (cancelled) return;
			const acceptedOffers = seekerOffers.filter((o: { status: string }) => o.status === 'accepted');
			let list = await getProjectsForSeeker(userIdNum).catch(() => []);
			if (cancelled) return;
			const projectOfferIds = new Set(list.map((p) => p.offerId).filter(Boolean).map(String));
			const toRepair = acceptedOffers.filter((o: { id: number }) => !projectOfferIds.has(String(o.id)));
			for (const offer of toRepair) {
				const offerId = typeof offer.id === 'string' ? parseInt(offer.id, 10) : offer.id;
				if (!isNaN(offerId)) {
					try {
						await ensureProjectForOffer(offerId);
					} catch {
						// ignore
					}
				}
				if (cancelled) return;
			}
			if (toRepair.length > 0) {
				list = await getProjectsForSeeker(userIdNum).catch(() => []);
				if (cancelled) return;
			}
			if (!cancelled) {
				setProjects(list);
				writeSeekerProjectsSession(userIdNum, list);
				setIsLoading(false);
			}
		};

		void loadAndRepair();
		return () => {
			cancelled = true;
		};
	}, [user?.id, seekerUserIdNum, viewAsTarget]);

	// Ensure every project card has an image: fetch plan by id when not in plansForImages
	useEffect(() => {
		if (!projects.length) return;
		let cancelled = false;
		projects.forEach((project) => {
			const planId = project.planId ? Number(project.planId) : NaN;
			if (isNaN(planId)) return;
			const inList = plansForImages.some((p) => p.id === planId);
			if (inList) return;
			const cachedThumb = readPlanThumbSession(planId);
			if (cachedThumb) {
				setProjectImageUrls((prev) => ({ ...prev, [project.id]: cachedThumb }));
				return;
			}
			getPlanById(planId)
				.then((pl) => {
					if (cancelled) return;
					const url =
						pl.after_image_url && !pl.after_image_url.includes('unsplash.com')
							? pl.after_image_url
							: pl.before_image_url ||
								'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop';
					const absolute = getImageUrl(url) || url;
					writePlanThumbSession(planId, absolute);
					setProjectImageUrls((prev) => ({ ...prev, [project.id]: absolute }));
				})
				.catch(() => {});
		});
		return () => { cancelled = true; };
	}, [projects, plansForImages]);

	const scopeCounts = useMemo(() => {
		let active = 0;
		let archive = 0;
		for (const p of projects) {
			if (projectMatchesListScope(p, 'active')) active += 1;
			if (projectMatchesListScope(p, 'archive')) archive += 1;
		}
		return { active, archive, total: projects.length };
	}, [projects]);

	const visibleProjects = useMemo(
		() => projects.filter((p) => projectMatchesListScope(p, listScope)),
		[projects, listScope]
	);

	if (isLoading) {
		return (
			<div className="d-flex align-items-center justify-content-center py-5">
				<div className="spinner-border text-success" role="status">
					<span className="visually-hidden">{copy('Loading projects...', 'Projekte werden geladen...', 'Učitavanje projekata...')}</span>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
				<div>
					<h1 className="h3 mb-1">{copy('My Projects', 'Meine Projekte', 'Moji projekti')}</h1>
					<p className="text-muted small mb-0">
						{copy('Projects from accepted offers. Track progress and stay in touch with your gardener.', 'Projekte aus angenommenen Angeboten. Verfolgen Sie den Fortschritt und bleiben Sie mit Ihrem Gärtner in Kontakt.', 'Projekti iz prihvaćenih ponuda. Pratite napredak i ostanite u kontaktu sa svojim vrtlarom.')}
					</p>
				</div>
			</div>

			{projects.length > 0 && (
				<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
					<div className="btn-group flex-wrap" role="group">
						<button
							type="button"
							className={`btn btn-sm ${listScope === 'active' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setListScope('active')}
						>
							<i className="bi bi-play-circle me-1" aria-hidden />
							{copy('Current', 'Aktuell', 'Trenutno')}
							<span className="badge bg-light text-dark ms-1">{scopeCounts.active}</span>
						</button>
						<button
							type="button"
							className={`btn btn-sm ${listScope === 'archive' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setListScope('archive')}
						>
							<i className="bi bi-archive me-1" aria-hidden />
							{copy('Past', 'Vergangen', 'Prošlo')}
							<span className="badge bg-light text-dark ms-1">{scopeCounts.archive}</span>
						</button>
						<button
							type="button"
							className={`btn btn-sm ${listScope === 'all' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setListScope('all')}
						>
							<i className="bi bi-list-ul me-1" aria-hidden />
							{copy('All', 'Alle', 'Sve')}
							<span className="badge bg-light text-dark ms-1">{scopeCounts.total}</span>
						</button>
					</div>
					<span className="text-muted small">
						{listScope === 'active'
							? copy(
									'Open projects only.',
									'Nur laufende Projekte.',
									'Samo aktivni projekti.'
								)
							: listScope === 'archive'
								? copy(
										'Completed or cancelled.',
										'Abgeschlossen oder storniert.',
										'Završeno ili otkazano.'
									)
								: copy('Everything in one list.', 'Alles in einer Liste.', 'Sve na jednom popisu.')}
					</span>
				</div>
			)}

			{projects.length === 0 ? (
				user?.role === 'admin' && Number.isNaN(seekerUserIdNum) ? (
					<div className="alert alert-info mb-0">
						{copy(
							'Choose a seeker in “Admin view” at the top to load their projects.',
							'Wählen Sie oben in „Admin-Ansicht“ einen Gartensuchenden, um dessen Projekte zu laden.',
							'Odaberite vlasnika vrta u „Admin prikazu“ na vrhu da učitate njegove projekte.'
						)}
					</div>
				) : (
				<div className="card">
					<div className="card-body text-center py-5">
						<p className="mb-3 text-muted">{copy("You don't have any projects yet.", 'Sie haben noch keine Projekte.', 'Još nemate nijedan projekt.')}</p>
						<p className="small text-muted mb-0">
							{copy('When you accept an offer on a plan (in My Plans -> Offers), it becomes a project and will appear here so you can track progress.', 'Wenn Sie ein Angebot für einen Plan annehmen (in Meine Pläne -> Angebote), wird daraus ein Projekt und es erscheint hier, damit Sie den Fortschritt verfolgen können.', 'Kada prihvatite ponudu za plan (u Moji planovi -> Ponude), ona postaje projekt i pojavit će se ovdje kako biste mogli pratiti napredak.')}
						</p>
						<Link to="/app/seeker/plans?tab=offers" className="btn btn-success mt-3">
							{copy('View my offers', 'Meine Angebote anzeigen', 'Prikaži moje ponude')}
						</Link>
					</div>
				</div>
				)
			) : visibleProjects.length === 0 ? (
				<div className="card border-0 shadow-sm">
					<div className="card-body text-center py-5">
						<p className="text-muted mb-3">
							{listScope === 'active' && scopeCounts.archive > 0
								? copy(
										'No open projects. Your finished work is under Past.',
										'Keine offenen Projekte. Abgeschlossenes finden Sie unter „Vergangen“.',
										'Nema otvorenih projekata. Završeno je pod „Prošlo“.'
									)
								: copy('Nothing in this list.', 'Nichts in dieser Liste.', 'Ništa na ovom popisu.')}
						</p>
						{listScope === 'active' && scopeCounts.archive > 0 && (
							<button
								type="button"
								className="btn btn-outline-success btn-sm"
								onClick={() => setListScope('archive')}
							>
								{copy('View past projects', 'Vergangene Projekte anzeigen', 'Prikaži prošle projekte')}
							</button>
						)}
					</div>
				</div>
			) : (
				<div className="row g-4">
					{visibleProjects.map((project) => {
						const projectCompanyDisplay = project.companyName || project.gardenerName;
						// Prefer image we fetched for this project; else plan from merged list; else placeholder
						const fallbackUrl = 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop';
						const plan = project.planId
							? plansForImages.find((p) => p.id === Number(project.planId))
							: undefined;
						const planImageRaw = projectImageUrls[project.id]
							? projectImageUrls[project.id]
							: plan?.after_image_url && !plan.after_image_url.includes('unsplash.com')
								? plan.after_image_url
								: plan?.before_image_url || fallbackUrl;
						const planImage = projectImageUrls[project.id] || getImageUrl(planImageRaw) || planImageRaw;
						const statusLabel =
							project.status === 'in_progress'
								? copy('In progress', 'In Bearbeitung', 'U tijeku')
								: project.status === 'planning'
									? copy('Planning', 'Planung', 'Planiranje')
									: project.status === 'quoted'
										? copy('Quoted', 'Angeboten', 'Ponuda poslana')
										: project.status === 'completed'
											? copy('Completed', 'Abgeschlossen', 'Završeno')
											: project.status === 'cancelled'
												? copy('Cancelled', 'Storniert', 'Otkazano')
												: project.status;
						return (
							<div key={project.id} className="col-12">
								<Link
									to={`/app/seeker/project/${project.id}`}
									className="text-decoration-none"
									style={{ color: 'inherit' }}
								>
									<div className="card border-0 shadow-sm overflow-hidden plan-card">
										<div className="row g-0">
											<div className="col-12 col-md-4">
												<div className="ratio ratio-4x3">
													<div
														className="w-100 h-100"
														style={{
															backgroundImage: `url(${planImage})`,
															backgroundSize: 'cover',
															backgroundPosition: 'center'
														}}
													>
														<div
															className="h-100 d-flex align-items-end p-3"
															style={{
																background:
																	'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)'
															}}
														>
															<span className="badge bg-primary">{statusLabel}</span>
														</div>
													</div>
												</div>
											</div>
											<div className="col-12 col-md-8">
												<div className="card-body p-4">
													<h2 className="h5 mb-2 fw-bold">{getProjectDisplayName(project)}</h2>
													<div className="d-flex flex-wrap gap-3 text-muted small mb-2">
														{projectCompanyDisplay && (
															<span>
																<i className="bi bi-building me-1"></i>
																{projectCompanyDisplay}
															</span>
														)}
														{project.dueDate && (
															<span>
																<i className="bi bi-calendar3 me-1"></i>
																{copy('Due', 'Fällig', 'Rok')} {new Date(project.dueDate).toLocaleDateString(locale)}
															</span>
														)}
														<span>
															<i className="bi bi-currency-euro me-1"></i>
															{project.budget.toLocaleString()} €
														</span>
													</div>
													<div className="d-flex flex-wrap gap-2">
														<span className="btn btn-success btn-sm">
															<i className="bi bi-eye me-1"></i>{copy('View project', 'Projekt ansehen', 'Prikaži projekt')}
														</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</Link>
							</div>
						);
					})}
				</div>
			)}
		</>
	);
}
